/**
 * Business Profile billing policy — paid AI analysis is explicit-user-only.
 * Automatic open/refresh/language triggers must never spend credits.
 */

export const BUSINESS_PROFILE_FEATURE = 'business_profile';
export const BUSINESS_PROFILE_OPERATION_KEY = 'analyze';
export const BUSINESS_PROFILE_EXPLICIT_CALLER = 'business-profile:explicit-analyze';

/** Situations that previously (or could) trigger silent paid analysis. */
export const AUTOMATIC_PROFILE_TRIGGERS = [
  'open_existing_project',
  'refresh_existing_project',
  'return_from_my_projects',
  'wait_after_project_load',
  'change_interface_language',
  'change_project_content_language',
  'load_existing_dream_country',
  'restore_saved_session',
  'rerender_dream_workspace',
] as const;

export type AutomaticProfileTrigger = (typeof AUTOMATIC_PROFILE_TRIGGERS)[number];

/** Paid Business Profile AI must never run for automatic lifecycle events. */
export function allowsAutomaticBusinessProfileGeneration(
  _trigger: AutomaticProfileTrigger | string
): boolean {
  return false;
}

/** Opening / refreshing / language changes cost zero credits. */
export function creditDeltaForAutomaticTrigger(
  _trigger: AutomaticProfileTrigger | string
): 0 {
  return 0;
}

/** One successful explicit generation costs exactly 1 credit. */
export function explicitBusinessProfileCreditCost(): 1 {
  return 1;
}

export function buildBusinessProfileIdempotencyKey(args: {
  projectId: string;
  attemptId: string;
}): string {
  const projectId = (args.projectId || 'pending').trim() || 'pending';
  const attemptId = args.attemptId.trim();
  return `business-profile:${projectId}:${attemptId}`;
}

export function businessProfileActionLabel(hasProfile: boolean): string {
  return hasProfile
    ? 'Regenerate Business Profile — 1 credit if successful'
    : 'Generate Business Profile — 1 credit if successful';
}

/** Non-blocking notice only — never auto-regenerate on language mismatch. */
export function shouldShowProfileLanguageMismatchNotice(
  profileLanguage: string | null | undefined,
  contentLanguage: string | null | undefined
): boolean {
  const profile = (profileLanguage ?? '').trim();
  const content = (contentLanguage ?? '').trim();
  if (!profile || !content) return false;
  return profile !== content;
}

export function profileLanguageMismatchNotice(
  profileLanguage: string,
  contentLanguage: string
): string {
  return `Saved Business Profile is in “${profileLanguage}”; project content language is “${contentLanguage}”. Regenerate to update — 1 credit if successful.`;
}
